import {createApi} from "@reduxjs/toolkit/query/react";
import {axiosBaseQuery} from "@/helpers/axiosBaseQuery";
import api from "@/store/api/api";
import {productsApi} from "@/store/api/v1/products";

export const storageApi = createApi({
    reducerPath: 'storageApi',
    tagTypes: ['storages', 'products'],
    baseQuery: axiosBaseQuery({
        baseUrl: api.root,
    }),
    endpoints: build => ({
        getStorages: build.query({
            query: (data) => ({
                url: api.storages.getStorages(),
                method: 'GET',
                params: data
            }),
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                return [endpointName, queryArgs?.location_id]
            },
            merge: (currentCache, newItems, {arg}) => {
                if (arg.skip === 0) {
                    return newItems;
                }
                currentCache.push(...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id)))
            },
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg !== previousArg;
            },
            providesTags: ['storages'],
        }),
        getStorageOptions: build.query({
            query: ({ queryOptions, ...params }) => ({
                url: api.storages.getStorages(),
                method: 'GET',
                params,
            }),
            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                return [endpointName + 'Options', queryArgs?.location_id + 'Options']
            },
            merge: (currentCache, newItems, {arg}) => {
                if (arg.skip === 0) {
                    return newItems;
                }
                currentCache.push(...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id)))
            },
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg !== previousArg;
            },
            providesTags: ['storages'],
        }),
        updateStorage: build.mutation({
            query: ({id, ...data}) => ({
                url: api.storages.updateStorage(id),
                method: 'PUT',
                body: data
            }),
            async onQueryStarted(patch, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled
                    dispatch(
                        storageApi.util.updateQueryData('getStorages', patch, (draft) => {
                            const index = draft?.findIndex((storage) => storage.id === patch.id);
                            if (index !== -1) {
                                draft[index] = {
                                    ...draft[index],
                                    ...patch,
                                }
                            }
                        })
                    );
                } catch (e) {
                    console.error('Update failed:', e)
                }
            },
        }),
        createStorage: build.mutation({
            query: (data) => ({
                url: api.storages.createStorage(),
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['storages']
        }),
        getStorageHistory: build.query({
            query: ({id, ...data}) => ({
                url: api.storages.getStorageHistory(id),
                method: 'GET',
                params: data
            }),
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                return [endpointName, queryArgs?.location_id]
            },
            merge: (currentCache, newItems, {arg}) => {
                if (arg.skip === 0) {
                    return newItems;
                }
                currentCache.push(...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id)))
            },
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg !== previousArg;
            },
        }),
        deleteStorage: build.mutation({
            query: ({id, ...data}) => ({
                url: api.storages.deleteStorage(id),
                method: 'DELETE',
                params: data
            }),
            async onQueryStarted(patch, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled
                    dispatch(
                        storageApi.util.updateQueryData('getStorages', {location_id: patch.location_id}, (draft) => {
                            const index = draft.findIndex((storage) => storage.id === patch.id);
                            if (index !== -1) {
                                draft.splice(index, 1)
                            } else {
                                console.warn('Storage not found in cache')
                            }
                        })
                    );
                } catch (e) {
                    console.error('Delete failed:', e)
                }
            },
        }),
        increaseProductsInStorage: build.mutation({
            query: ({id, products}) => ({
                url: api.storages.increaseProducts(id),
                method: 'POST',
                body: products,
            }),
            async onQueryStarted(args, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;

                    dispatch(
                        productsApi.endpoints.getProducts.initiate(
                            {
                                storage_id: args.id,
                                location_id: args.location_id,
                                skip: 0
                            },
                            {forceRefetch: true}
                        )
                    );
                } catch (error) {
                    console.error('Failed to trigger getProducts:', error);
                }
            },
        }),
        decreaseProductsInStorage: build.mutation({
            query: ({id, products}) => ({
                url: api.storages.decreaseProducts(id),
                method: 'POST',
                body: products,
            }),
            async onQueryStarted(args, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;

                    dispatch(
                        productsApi.endpoints.getProducts.initiate(
                            {
                                storage_id: args.id,
                                location_id: args.location_id,
                                skip: 0
                            },
                            {forceRefetch: true}
                        )
                    );
                } catch (error) {
                    console.error('Failed to trigger getProducts:', error);
                }
            },
        }),
        detachProductInStorage: build.mutation({
            query: ({storage_id, product_id, ...data}) => ({
                url: api.storages.detachProducts(storage_id, product_id),
                method: 'DELETE',
                params: data
            }),
            async onQueryStarted(args, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;

                    dispatch(
                        productsApi.endpoints.getProducts.initiate(
                            {
                                storage_id: args.storage_id,
                                location_id: args.location_id,
                                skip: 0
                            },
                            {forceRefetch: true}
                        )
                    );
                } catch (error) {
                    console.error('Failed to trigger getProducts:', error);
                }
            },
        }),
    })
})

export const {useGetStoragesQuery, useDeleteStorageMutation} = storageApi
